import React, { useCallback, useEffect, useRef, useState } from "react";

import { Card, Divider, Pagination, Stack } from "@mui/material";
import EmptyCardDataOperationsDebtPayments from "assets/images/EmptyCardDataOperationsDebtPayments.png";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { EmptyCard } from "components/Empty-card/EmptyCard";
import { InlineFilter } from "components/Filters/InlineFilter";
import { SelectCurrency } from "components/reports/Select-currency/SelectCurrency";
import { EMPTY_CARD_CONTENT } from "constants/empty-card";
import { useActionHook, useNavigationHooks, useUrlHooks } from "hooks";
import { isEqual } from "lodash";
import { useDebtActions, useReportsActions, useReportsQueriesSelector } from "store";
import { CurrencyType, ExchangeMethod, ReportFilters } from "store/types";

import { AmortizationTable } from "../Deal-amortization/AmortizationTable";

interface AmortizationStatePayload {
  filters: ReportFilters;
  currency?: CurrencyType;
  exchangeMethod?: ExchangeMethod;
}

export const CombinedAmortizationTable: React.FC = () => {
  const { getCombinedAmortizationTable } = useDebtActions();
  const { getFilters, setQueriesAction } = useReportsActions();
  const { currency, exchangeMethod } = useReportsQueriesSelector();
  const { queryParams } = useUrlHooks();
  const { setParams, removeParams } = useNavigationHooks();

  const [page, setPage] = useState(0);
  const rowsPerPage = 50;

  const currencyRef = useRef(currency);
  const exchangeMethodRef = useRef(exchangeMethod);
  useEffect(() => {
    currencyRef.current = currency;
    exchangeMethodRef.current = exchangeMethod;
  });

  const [filters, setFilters] = useState<ReportFilters>(() => {
    const dataOperations = queryParams.dataOperations as string | undefined;
    if (dataOperations) {
      try {
        return (JSON.parse(dataOperations) as AmortizationStatePayload).filters || {};
      } catch {
        // fall through
      }
    }
    return {};
  });

  // State → URL
  useEffect(() => {
    const hasFilters = Object.keys(filters).length > 0;
    if (!hasFilters && !currency && !exchangeMethod) {
      removeParams(["dataOperations"]);
      return;
    }
    const payload: AmortizationStatePayload = { filters, currency, exchangeMethod };
    setParams({ search: { dataOperations: JSON.stringify(payload) } });
  }, [filters, currency, exchangeMethod]);

  // URL → state. Runs on mount (so bookmark currency/exchangeMethod apply once)
  // and on every URL change (so switching bookmarks while on this page updates state).
  useEffect(() => {
    const dataOperations = queryParams.dataOperations as string | undefined;
    if (!dataOperations) {
      setFilters((prev) => (Object.keys(prev).length ? {} : prev));
      return;
    }
    try {
      const fromUrl = JSON.parse(dataOperations) as AmortizationStatePayload;
      setFilters((prev) => (isEqual(prev, fromUrl.filters) ? prev : fromUrl.filters || {}));

      const queriesPatch: Partial<{ currency: CurrencyType; exchangeMethod: ExchangeMethod }> = {};
      if (fromUrl.currency && fromUrl.currency !== currencyRef.current) queriesPatch.currency = fromUrl.currency;
      if (fromUrl.exchangeMethod && fromUrl.exchangeMethod !== exchangeMethodRef.current) {
        queriesPatch.exchangeMethod = fromUrl.exchangeMethod;
      }
      if (Object.keys(queriesPatch).length) setQueriesAction(queriesPatch);
    } catch {
      // ignore
    }
  }, [queryParams.dataOperations, setQueriesAction]);

  const {
    action: fetchAmortizationTable,
    loading,
    data: amortizationData
  } = useActionHook(getCombinedAmortizationTable);
  const { data: filtersList, action: fetchFilters } = useActionHook(getFilters);

  const getAmortizationFilters = useCallback(
    async (filters?: ReportFilters) => {
      await fetchFilters(filters || {});
    },
    [fetchFilters]
  );

  useEffect(() => {
    getAmortizationFilters(filters);
  }, []);

  useEffect(() => {
    fetchAmortizationTable({
      filters,
      currency,
      exchange_date: exchangeMethod
    });
    if (Object.keys(filters).length) {
      getAmortizationFilters(filters);
    }
    setPage(0);
  }, [filters, currency, exchangeMethod]);

  return (
    <Card sx={{ p: 5, flexGrow: 1 }}>
      <Stack sx={{ gap: "15px", flexGrow: 1, height: "100%" }}>
        <Stack direction="row" sx={{ gap: "10px", alignItems: "center" }}>
          <InlineFilter
            state={filters}
            filtersList={filtersList || {}}
            handleChangeFilter={setFilters}
            applyMode={true}
            refetchFilters={getAmortizationFilters}
          />
          <Divider sx={{ height: "25px", width: "1px", bgcolor: "#b6b7bf" }} />
          <SelectCurrency />
        </Stack>
        <AsyncContentMui isLoading={loading}>
          {!amortizationData || !amortizationData.rows.length ? (
            <EmptyCard
              title={EMPTY_CARD_CONTENT.combinedAmortization.title}
              description={{
                text: EMPTY_CARD_CONTENT.combinedAmortization.description,
                externalText: EMPTY_CARD_CONTENT.combinedAmortization.externalDescription,
                sx: { width: "380px" }
              }}
              image={{
                src: EmptyCardDataOperationsDebtPayments,
                sx: { width: 315, height: 140 }
              }}
              fitPage
            />
          ) : (
            <>
              <AmortizationTable
                table={amortizationData}
                rows={amortizationData.rows
                  .filter((row) => !row.is_report)
                  .slice(page * rowsPerPage, (page + 1) * rowsPerPage)}
                sx={{ maxHeight: "calc(100vh - 280px)" }}
              />
              <Pagination
                count={Math.ceil(amortizationData.rows.filter((row) => !row.is_report).length / rowsPerPage)}
                page={page + 1}
                onChange={(_, newPage) => setPage(newPage - 1)}
                sx={{ alignSelf: "center", mt: 1 }}
              />
            </>
          )}
        </AsyncContentMui>
      </Stack>
    </Card>
  );
};
